import contract from "./housingHtsContract.generated.ts";
import type { HousingRtmsScopeId } from "./adapters/housingRtms";

export type HousingHtsScopeId = HousingRtmsScopeId;
export type HousingViewportPreset = "1M" | "3M" | "6M" | "1Y" | "MAX";
export type HousingMonthlyPrimary = "rtms_self_index" | "official_index";
export type HousingTapeCapability = "BOUNDED_ON_DEMAND" | "PENDING_BOUNDED_ON_DEMAND" | "UNAVAILABLE_UNTIL_SDP_COVERAGE";
export type HousingCanonicalHistoryConsumerMode = "LEGACY_PRIMARY_REPOSITORY_CANONICAL_PENDING" | "REPOSITORY_CANONICAL_PRIMARY";

export interface HousingHtsScopeCapability {
  id: HousingHtsScopeId;
  label: string;
  rtmsPrimary: "AVAILABLE" | "PENDING_SDP_COVERAGE";
  monthlyPrimary: HousingMonthlyPrimary;
  officialBenchmark: string | null;
  detailedChartDocument: string | null;
  supportedResolutions: string[];
  tapeCapability: HousingTapeCapability;
}

export interface HousingTapeBandDefinition {
  id: string;
  label: string;
  minPriceExclusive?: number;
  minPriceInclusive?: number;
  maxPriceExclusive?: number;
  maxPriceInclusive?: number;
}

export interface HousingTapeContract {
  mode: "GENERATION_PINNED_ON_DEMAND";
  datasetId: "rtms.apt.trade";
  scopePolicy: "DETAILED_CHART_SCOPES_ONLY";
  datePolicy: "SAME_CALENDAR_MONTH_ONLY";
  rawRowsInAnalysisCache: false;
  rawRowsInBrowserCache: false;
  maxRows: number;
  maxPartitions: number;
  maxRequests: number;
  maxConcurrency: number;
  maxArtifactBytes: number;
  maxAggregateBytes: number;
  revisionPolicy: "PINNED_ARTIFACT_CONTENT_REQUIRED";
  archiveReceiptPolicy: "UNAVAILABLE_WITHOUT_EXACT_MEMBER_ENDPOINT";
  bands: readonly HousingTapeBandDefinition[];
}

const KNOWN_IDS = new Set<HousingHtsScopeId>(["national", "capital", "noncapital", "seoul", "gyeonggi", "incheon", "seoul_non_gangnam", "gangnam3", "prime4"]);
const rawTargetArchitecture = (contract.hts as unknown as Record<string, unknown>).targetArchitecture;
if (!rawTargetArchitecture || typeof rawTargetArchitecture !== "object" || Array.isArray(rawTargetArchitecture)) throw new Error("invalid_housing_hts_target_architecture");
const rawCanonicalHistory = (rawTargetArchitecture as Record<string, unknown>).canonical1dHistory;
if (!rawCanonicalHistory || typeof rawCanonicalHistory !== "object" || Array.isArray(rawCanonicalHistory)) throw new Error("invalid_housing_canonical_history_contract");
const canonicalHistory = rawCanonicalHistory as Record<string, unknown>;
const canonicalConsumerMode = String(canonicalHistory.consumerMode ?? "");
const canonicalReferenceSemanticVersion = Number(canonicalHistory.referenceSemanticVersion);
const canonicalRepositoryBasePath = String(canonicalHistory.runtimeBasePath ?? "");
const canonicalRepositoryCacheReady = canonicalHistory.cacheReady === true;
const expectedLegacyFallback = canonicalConsumerMode === "REPOSITORY_CANONICAL_PRIMARY"
  ? "NO_EXTERNAL_HOUSING_RUNTIME_FALLBACK_AFTER_APPROVED_CUTOVER"
  : "KEEP_LEGACY_PRIMARY_UNTIL_EXPLICIT_CUTOVER_APPROVAL";
const expectedPrimaryFallback = canonicalConsumerMode === "REPOSITORY_CANONICAL_PRIMARY"
  ? "FAIL_CLOSED_REPOSITORY_LAST_KNOWN_GOOD_NO_EXTERNAL_FALLBACK"
  : "FAIL_CLOSED_KEEP_LEGACY_UNTIL_EXPLICIT_CUTOVER_APPROVAL";
if (!["LEGACY_PRIMARY_REPOSITORY_CANONICAL_PENDING", "REPOSITORY_CANONICAL_PRIMARY"].includes(canonicalConsumerMode) ||
    canonicalHistory.canonicalPrimaryRequiresExplicitApproval !== true ||
    canonicalHistory.legacyFallback !== expectedLegacyFallback ||
    canonicalHistory.canonicalPrimaryFallback !== expectedPrimaryFallback ||
    canonicalHistory.shadowReadPublicationEffect !== "NONE" ||
    canonicalHistory.cacheReadinessDoesNotAuthorizeCutover !== true ||
    canonicalHistory.cacheMode !== "REPOSITORY_FILES_ONLY" ||
    canonicalHistory.repositoryCacheRoot !== "public/data/housing/canonical-1d" ||
    canonicalRepositoryBasePath !== "/data/housing/canonical-1d" ||
    canonicalHistory.externalPersistenceAllowed !== false ||
    canonicalHistory.cloudflareD1R2Allowed !== false ||
    canonicalHistory.runtimeWritesAllowed !== false ||
    !Number.isInteger(canonicalReferenceSemanticVersion) || canonicalReferenceSemanticVersion < 1) {
  throw new Error("invalid_housing_canonical_history_consumer_contract");
}
if (canonicalConsumerMode === "REPOSITORY_CANONICAL_PRIMARY" && !canonicalRepositoryCacheReady) throw new Error("canonical_repository_primary_without_cache");
export const HOUSING_CANONICAL_HISTORY_CONSUMER_MODE = canonicalConsumerMode as HousingCanonicalHistoryConsumerMode;
export const HOUSING_CANONICAL_HISTORY_REFERENCE_SEMANTIC_VERSION = canonicalReferenceSemanticVersion;
export const HOUSING_CANONICAL_REPOSITORY_CACHE_READY = canonicalRepositoryCacheReady;
export const HOUSING_CANONICAL_REPOSITORY_BASE_PATH = canonicalRepositoryBasePath;
const rawScopes = contract.hts.scopes as Array<Record<string, unknown>>;
if (!Array.isArray(rawScopes) || rawScopes.length !== KNOWN_IDS.size) throw new Error("invalid_housing_hts_scope_registry");

const seen = new Set<string>();
export const HOUSING_HTS_SCOPES: readonly HousingHtsScopeCapability[] = Object.freeze(rawScopes.map((raw): HousingHtsScopeCapability => {
  const id = String(raw.id ?? "") as HousingHtsScopeId;
  if (!KNOWN_IDS.has(id) || seen.has(id)) throw new Error(`invalid_housing_hts_scope_id:${id}`);
  seen.add(id);
  const rtmsPrimary = raw.rtmsPrimary;
  const monthlyPrimary = raw.monthlyPrimary;
  const detailedChartDocument = raw.detailedChartDocument == null ? null : String(raw.detailedChartDocument);
  const officialBenchmark = raw.officialBenchmark == null ? null : String(raw.officialBenchmark);
  const supportedResolutions = raw.supportedResolutions;
  const tapeCapability = raw.tapeCapability;
  if ((rtmsPrimary !== "AVAILABLE" && rtmsPrimary !== "PENDING_SDP_COVERAGE") ||
      (monthlyPrimary !== "rtms_self_index" && monthlyPrimary !== "official_index") ||
      !Array.isArray(supportedResolutions) || !supportedResolutions.length || supportedResolutions.some(value => typeof value !== "string") ||
      !["BOUNDED_ON_DEMAND", "PENDING_BOUNDED_ON_DEMAND", "UNAVAILABLE_UNTIL_SDP_COVERAGE"].includes(String(tapeCapability))) {
    throw new Error(`invalid_housing_hts_scope_capability:${id}`);
  }
  if (monthlyPrimary === "official_index" && rtmsPrimary === "AVAILABLE") throw new Error(`invalid_housing_hts_official_primary:${id}`);
  if (detailedChartDocument && detailedChartDocument !== `chart-${id}`) throw new Error(`invalid_housing_hts_chart_document:${id}`);
  if (tapeCapability === "BOUNDED_ON_DEMAND" && !detailedChartDocument) throw new Error(`invalid_housing_hts_tape_scope:${id}`);
  return {
    id,
    label: String(raw.label ?? id),
    rtmsPrimary,
    monthlyPrimary,
    officialBenchmark,
    detailedChartDocument,
    supportedResolutions: [...supportedResolutions] as string[],
    tapeCapability: tapeCapability as HousingTapeCapability
  };
}));

function positiveInteger(value: unknown, label: string) {
  const n = Number(value);
  if (!Number.isInteger(n) || n <= 0) throw new Error(`invalid_housing_tape_${label}`);
  return n;
}

const rawTape = contract.hts.tape as Record<string, unknown>;
const rawBands = rawTape.bands;
if (!rawTape || rawTape.mode !== "GENERATION_PINNED_ON_DEMAND" || rawTape.datasetId !== "rtms.apt.trade" ||
    rawTape.scopePolicy !== "DETAILED_CHART_SCOPES_ONLY" || rawTape.datePolicy !== "SAME_CALENDAR_MONTH_ONLY" ||
    rawTape.rawRowsInAnalysisCache !== false || rawTape.rawRowsInBrowserCache !== false ||
    rawTape.revisionPolicy !== "PINNED_ARTIFACT_CONTENT_REQUIRED" ||
    rawTape.archiveReceiptPolicy !== "UNAVAILABLE_WITHOUT_EXACT_MEMBER_ENDPOINT" ||
    !Array.isArray(rawBands) || !rawBands.length) {
  throw new Error("invalid_housing_tape_contract");
}
const tapeBands = rawBands.map((raw): HousingTapeBandDefinition => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) throw new Error("invalid_housing_tape_band");
  const x = raw as Record<string, unknown>;
  const id = String(x.id ?? "").trim(), label = String(x.label ?? "").trim();
  if (!id || !label) throw new Error("invalid_housing_tape_band");
  const out: HousingTapeBandDefinition = { id, label };
  for (const key of ["minPriceExclusive", "minPriceInclusive", "maxPriceExclusive", "maxPriceInclusive"] as const) {
    if (x[key] != null) {
      const value = Number(x[key]);
      if (!Number.isFinite(value) || value <= 0) throw new Error(`invalid_housing_tape_band_${id}`);
      out[key] = value;
    }
  }
  return out;
});
if (tapeBands[0]?.id !== "all" || new Set(tapeBands.map(band => band.id)).size !== tapeBands.length) throw new Error("invalid_housing_tape_bands");

export const HOUSING_TAPE_CONTRACT: Readonly<HousingTapeContract> = Object.freeze({
  mode: "GENERATION_PINNED_ON_DEMAND",
  datasetId: "rtms.apt.trade",
  scopePolicy: "DETAILED_CHART_SCOPES_ONLY",
  datePolicy: "SAME_CALENDAR_MONTH_ONLY",
  rawRowsInAnalysisCache: false,
  rawRowsInBrowserCache: false,
  maxRows: positiveInteger(rawTape.maxRows, "max_rows"),
  maxPartitions: positiveInteger(rawTape.maxPartitions, "max_partitions"),
  maxRequests: positiveInteger(rawTape.maxRequests, "max_requests"),
  maxConcurrency: positiveInteger(rawTape.maxConcurrency, "max_concurrency"),
  maxArtifactBytes: positiveInteger(rawTape.maxArtifactBytes, "max_artifact_bytes"),
  maxAggregateBytes: positiveInteger(rawTape.maxAggregateBytes, "max_aggregate_bytes"),
  revisionPolicy: "PINNED_ARTIFACT_CONTENT_REQUIRED",
  archiveReceiptPolicy: "UNAVAILABLE_WITHOUT_EXACT_MEMBER_ENDPOINT",
  bands: Object.freeze(tapeBands)
});
if (HOUSING_TAPE_CONTRACT.maxRequests < HOUSING_TAPE_CONTRACT.maxPartitions ||
    HOUSING_TAPE_CONTRACT.maxConcurrency > HOUSING_TAPE_CONTRACT.maxRequests ||
    HOUSING_TAPE_CONTRACT.maxArtifactBytes > HOUSING_TAPE_CONTRACT.maxAggregateBytes) {
  throw new Error("invalid_housing_tape_budget_contract");
}

export const HOUSING_HTS_SCOPE_IDS = Object.freeze(HOUSING_HTS_SCOPES.map(scope => scope.id));
export const HOUSING_HTS_SCOPE_BY_ID = Object.freeze(Object.fromEntries(HOUSING_HTS_SCOPES.map(scope => [scope.id, scope])) as Record<HousingHtsScopeId, HousingHtsScopeCapability>);
export const HOUSING_RTMS_SCOPE_IDS = Object.freeze(HOUSING_HTS_SCOPES.filter(scope => scope.rtmsPrimary === "AVAILABLE").map(scope => scope.id as HousingRtmsScopeId));
export const HOUSING_SELF_INDEX_SCOPE_IDS = Object.freeze(HOUSING_HTS_SCOPES.filter(scope => scope.monthlyPrimary === "rtms_self_index").map(scope => scope.id as HousingRtmsScopeId));
export const HOUSING_CANONICAL_1D_HISTORY_SCOPES = Object.freeze(HOUSING_HTS_SCOPES.filter(scope => scope.supportedResolutions.includes("1d")).map(scope => scope.id as HousingRtmsScopeId));
export const HOUSING_OFFICIAL_PRICE_IDS = Object.freeze(HOUSING_HTS_SCOPES.flatMap(scope => scope.officialBenchmark ? [scope.officialBenchmark] : []));
export const HOUSING_DETAILED_CHART_SCOPES = Object.freeze(HOUSING_HTS_SCOPES.flatMap(scope => scope.detailedChartDocument ? [scope.id as HousingRtmsScopeId] : []));
export const HOUSING_DETAILED_CHART_DOCUMENTS = Object.freeze(HOUSING_HTS_SCOPES.flatMap(scope => scope.detailedChartDocument ? [scope.detailedChartDocument] : []));
export const HOUSING_VIEWPORT_PRESETS = Object.freeze(contract.hts.viewportPresets as HousingViewportPreset[]);
